export type DiagnosticFields = {
  request_id?: string;
  method?: string;
  path?: string;
  http_status?: number;
  response_type?: string;
  failed_stage?: string;
  exception_type?: string;
  cause_type?: string;
  location?: string;
  reason?: string;
  upstream_request_id?: string;
  upstream_code?: string;
  shot_id?: string;
  shot_number?: number;
  completed?: number;
  total?: number;
  retry_after?: number;
};
export type DiagnosticTask = {
  usage?: { performance?: Record<string, number>; reported_calls?: number };
  id: string;
  type: string;
  status: string;
  stage?: string;
  created_at: number;
  project_id?: string;
  request_id?: string;
  request?: { method?: string; path?: string };
  model_context?: { provider?: string; model?: string; endpoint_host?: string };
  diagnostics?: DiagnosticFields;
  error?: { code: string; message: string } | null;
  local_submission?: boolean;
  provider_job_id?: string;
  logs?: { at: number; stage: string; status: string; message?: string }[];
};
export function safeDiagnosticText(value: string) {
  return value
    .replace(/Bearer\s+\S+|sk-[\w-]{8,}/gi, "[已隐藏凭据]")
    .replace(
      /((?:api[_ -]?key|token|password|secret|authorization|cookie)["']?\s*[=:]\s*["']?)[^\s"',;}]+/gi,
      "$1[已隐藏]",
    )
    .replace(/https?:\/\/[^\s]+/g, (url) => {
      try {
        return new URL(url).hostname;
      } catch {
        return "接口地址";
      }
    })
    .slice(0, 600);
}
export function cleanDiagnostics(value: unknown): DiagnosticFields {
  if (!value || typeof value !== "object") return {};
  const allowed = [
    "request_id",
    "method",
    "path",
    "http_status",
    "response_type",
    "retry_after",
    "failed_stage",
    "exception_type",
    "cause_type",
    "location",
    "reason",
    "upstream_request_id",
    "upstream_code",
    "shot_id",
    "shot_number",
    "completed",
    "total",
  ];
  return Object.fromEntries(
    Object.entries(value)
      .filter(
        ([key, item]) =>
          allowed.includes(key) && ["string", "number"].includes(typeof item),
      )
      .map(([key, item]) => [
        key,
        typeof item === "string" ? safeDiagnosticText(item) : item,
      ]),
  );
}
export const taskStages: Record<string, string> = {
  submitting: "提交任务",
  queued: "等待执行",
  processing: "处理素材",
  cloud_upload: "上传素材到云端",
  cloud_verifying: "校验云端文件",
  probing: "读取视频信息",
  transcoding: "生成预览视频",
  extracting_poster: "提取视频封面",
  extracting_audio: "提取原始音频",
  preparing_asr: "准备识别音频",
  recognizing: "识别目标帧与素材",
  transcribing: "识别口播",
  segmenting: "划分分镜",
  planning: "规划效果",
  matting: "处理人物遮挡",
  rendering: "渲染画面",
  video: "合成预览视频",
  encoding: "编码视频",
  saving: "保存结果",
  completed: "处理完成",
  failed: "处理异常",
  cancelled: "已取消",
  interrupted: "执行中断",
  synthesizing: "生成配音",
  proofreading: "校对文案",
  downloading: "下载素材",
  validating: "验证文件",
  loading_model: "加载模型",
  generating_shots: "逐镜制作",
  provider_processing: "服务商处理中",
};
export function failedStage(task: DiagnosticTask) {
  return (
    task.diagnostics?.failed_stage ||
    [...(task.logs || [])]
      .reverse()
      .find(
        (entry) =>
          ![
            "failed",
            "cancelled",
            "completed",
            "未完成",
            "已完成",
            "已中断",
          ].includes(entry.stage),
      )?.stage ||
    task.stage ||
    "未记录"
  );
}
export function troubleshootingText(task: DiagnosticTask) {
  return JSON.stringify(
    {
      task_id: task.local_submission ? undefined : task.id,
      project_id: task.project_id,
      type: task.type,
      status: task.status,
      created_at: new Date(task.created_at * 1000).toISOString(),
      request_id: task.request_id || task.diagnostics?.request_id,
      provider_job_id: task.provider_job_id,
      error: task.error
        ? {
            code: safeDiagnosticText(task.error.code),
            message: safeDiagnosticText(task.error.message),
          }
        : undefined,
      failed_stage: task.status === "failed" ? failedStage(task) : undefined,
      request: task.request,
      model: task.model_context,
      performance: task.usage?.performance,
      diagnostics: cleanDiagnostics(task.diagnostics),
      logs: task.logs?.map((entry) => ({
        ...entry,
        message: entry.message ? safeDiagnosticText(entry.message) : undefined,
      })),
    },
    null,
    2,
  );
}
